/**
 * CustomerDropdownsHub — Feb 2026 (User request E)
 * =================================================
 *
 * Landing hub for the "قوائم منسدلة للتحكم بمنصة العملاء" area, sitting at
 *   /control/cloudgm/customer-dropdowns
 *
 * Previously that URL jumped straight into the customer-scope
 * ServiceCatalogHub. The user wants a hub that groups the different
 * dropdown catalogs — starting with two, but future-proofed for more:
 *
 *   ┌────────────────────────────┐   ┌────────────────────────────┐
 *   │ 🟢 مسمى الخدمة للإدخال     │   │ 🔵 مسمى الخدمة لإدخال       │
 *   │    اليدوي (Customer form)  │   │    المهام (يدوي)            │
 *   └────────────────────────────┘   └────────────────────────────┘
 *
 * Each card opens a full ServiceCatalogHub instance in its own scope.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ListChecks, User, Store, ChevronLeft, Ban, Phone, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { usePerms } from '../permissions/PermissionsContext';

const HUB_CARDS = [
  {
    id: 'manual-entry',
    testid: 'dropdown-hub-manual-entry',
    labelAr: 'مسمى الخدمة الرئيسية والفرعية لمنصة العملاء',
    labelEn: 'Main & Sub Service Names (Customer Platform)',
    descAr: 'مسميات الخدمات الرئيسية والفرعية اللي تظهر داخل بطاقات الخدمات (التأشيرة، المرور، الضريبة، توثيق المستندات، الترجمة القانونية) وفي إنشاء الفواتير.',
    descEn: 'Main + sub service names shown inside service cards (visa, traffic, tax, doc-auth, legal-translation) and invoice creation forms.',
    path: '/control/cloudgm/customer-dropdowns/manual-entry',
    Icon: User,
    ringClass: 'hover:border-cyan-500/60',
    accentBg: 'bg-cyan-500/10',
    accentBorder: 'border-cyan-500/30',
    accentText: 'text-cyan-400',
  },
  // iter-269 (Feb 2026) — CGC customer ban reasons management
  {
    id: 'ban-reasons',
    testid: 'dropdown-hub-cgc-ban-reasons',
    labelAr: 'أسباب حظر الحساب (منصة العملاء)',
    labelEn: 'Customer Ban Reasons',
    descAr: 'قائمة الأسباب التي تظهر عند حظر عميل — يمكنك إضافة وترجمة أسباب جديدة.',
    descEn: 'Reasons shown when blocking a customer — admin can add and translate new ones.',
    path: '/control/cloudgm/customer-dropdowns/ban-reasons',
    Icon: Ban,
    ringClass: 'hover:border-red-500/60',
    accentBg: 'bg-red-500/10',
    accentBorder: 'border-red-500/30',
    accentText: 'text-red-400',
  },
  // Jun 2026 — mobile operators + 3-digit prefixes (read by CGC & CGB with server key)
  {
    id: 'phone-operators',
    testid: 'dropdown-hub-phone-operators',
    labelAr: 'قوائم مشغّلي أرقام الهواتف',
    labelEn: 'Phone Operators Lists',
    descAr: 'اسم المزوّد (عربي/إنجليزي) وتحته أكواده المكوّنة من 3 أرقام مثل 050 — تظهر عند اختيار المشغّل في إعلانات الأرقام بمنصّتي العملاء والتجار.',
    descEn: 'Operator name (AR/EN) with its 3-digit prefixes like 050 — shown when picking the operator in phone-number ads on the customer & merchant platforms.',
    path: '/control/cloudgm/customer-dropdowns/phone-operators',
    Icon: Phone,
    ringClass: 'hover:border-emerald-500/60',
    accentBg: 'bg-emerald-500/10',
    accentBorder: 'border-emerald-500/30',
    accentText: 'text-emerald-400',
  },
  // Jun 2026 — partner offices used by "رسوم إضافية لمكتب آخر" in per-emirate service pricing
  {
    id: 'partner-offices',
    testid: 'dropdown-hub-partner-offices',
    labelAr: 'قائمة مكاتب الشركاء',
    labelEn: 'Partner Offices List',
    descAr: 'أسماء المكاتب الشريكة (عربي/إنجليزي مع ترجمة الذكاء الاصطناعي) — تظهر عند اختيار «رسوم إضافية لمكتب آخر» في أسعار الإمارات داخل الخدمات.',
    descEn: 'Partner office names (AR/EN with AI translate) — shown when choosing "Extra fee for another office" in per-emirate service pricing.',
    path: '/control/cloudgm/customer-dropdowns/partner-offices',
    Icon: Building2,
    ringClass: 'hover:border-amber-500/60',
    accentBg: 'bg-amber-500/10',
    accentBorder: 'border-amber-500/30',
    accentText: 'text-amber-400',
  },
  // iter-247 (Feb 2026) — REMOVED the "Manual Task Entry Service Name"
  // card from the customer hub. It's been moved to the merchants hub
  // (`/control/cloudgm-business/dropdown-lists`) since manual agent
  // tasks live semantically under the merchant/agent domain, not the
  // customer domain. Route `/control/cloudgm/customer-dropdowns/manual-tasks`
  // has been removed too (see App.js).
];

export default function CustomerDropdownsHub() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();
  const { visiblePages } = usePerms();

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath="/control/cloudgm" />

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'القوائم المنسدلة للتحكم بمنصة العملاء', label_en: 'Customer Platform Dropdown Lists', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex items-start gap-3">
          <div className="w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center flex-shrink-0">
            <ListChecks className="w-7 h-7 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {t('القوائم المنسدلة للتحكم بمنصة العملاء', 'Customer Platform Dropdown Lists')}
            </h1>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              {t(
                'اختر القائمة اللي تبي تعدلها. كل بطاقة تفتح مسمى الخدمة الخاص بها (رئيسي + فرعي) وتقدر تضيف/تعدل/تحذف من هناك.',
                'Pick the catalog you want to edit. Each card opens its own service names (main + sub) hub.',
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visiblePages(HUB_CARDS, 'path').map((c, i) => (
            <motion.button
              key={c.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              whileHover={{ y: -4, scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              onClick={() => navigate(c.path)}
              data-testid={c.testid}
              className={`text-start bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 ${c.ringClass} transition-all duration-300 cursor-pointer`}
            >
              <div className={`w-14 h-14 rounded-xl ${c.accentBg} border ${c.accentBorder} flex items-center justify-center mb-4`}>
                <c.Icon className={`w-7 h-7 ${c.accentText}`} strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">
                {t(c.labelAr, c.labelEn)}
              </h3>
              <p className="text-xs text-white/50 leading-relaxed">
                {t(c.descAr, c.descEn)}
              </p>
              <div className={`mt-4 flex items-center gap-1 text-xs font-medium ${c.accentText}`}>
                <span>{t('فتح القائمة', 'Open catalog')}</span>
                <ChevronLeft className={`w-4 h-4 ${isAr ? '' : 'rotate-180'}`} />
              </div>
            </motion.button>
          ))}
        </div>
      </main>
    </div>
  );
}
